import React, { useCallback, useState } from "react";
import "./styles/Login.css";
import { AuthService } from "../services/AuthService";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../common/app-routes";
import { AuthSectionNames } from "../common/nav-sections";
import AuthForm from "./AuthForm";
import { Validator } from "../services/Validator";
import toast from "react-hot-toast";
import { APP_DOMAIN, APP_NAME, DarkOrLightType } from "../common/constants";
import SEO from "./SEO";
import { isSuccess } from "../common/apiResult";
import GoogleAuth from "./GoogleAuth";

interface RegisterProps {
  changeActiveSection: (id: string) => void;
  theme: DarkOrLightType
}

const Register: React.FC<RegisterProps> = ({ changeActiveSection, theme }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAuthError = useCallback((error: { message?: string } | undefined) => {
    const errorMessage = error?.message ?? "Something went wrong";
    toast.error(errorMessage)
    setError(errorMessage);
    setIsLoading(false);
  }, [setError, setIsLoading]);

  const handleRegister = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!Validator.validateRegister(email, password, confirmPassword, setError)) {
      return;
    }

    setIsLoading(true);
    setError("");

    const response = await AuthService.register(email, password, confirmPassword);

    if (!isSuccess(response)) {
      handleAuthError(response.error);

      return;
    }

    toast.success("Account successfully created! 🎉");

    navigate(`/${AuthSectionNames.LOGIN}`);
    changeActiveSection(AuthSectionNames.LOGIN);

    setIsLoading(false);
  }, [navigate, email, password, confirmPassword, changeActiveSection, setIsLoading, handleAuthError]);

  const onGoogleAuthenticationSuccess = useCallback(async (googleToken: string) => {
    const response = await AuthService.loginWithGoogle(googleToken);

    if (!isSuccess(response)) {
      handleAuthError(response.error);

      return;
    }

    toast.success("Account successfully created! 🎉");

    changeActiveSection(AuthSectionNames.LOGIN);
    navigate(AppRoutes.LOGIN);
  }, [navigate, changeActiveSection, handleAuthError])

  return (
    <>
      <SEO
        title={`Register | ${APP_NAME}`}
        description={`Create your ${APP_NAME} account and start processing media faster. Email + Google login supported.`}
        url={`${APP_DOMAIN}${AppRoutes.REGISTER}`}
      />
      <div className="login-container">
        <h1 className="login-title">Create Your {APP_NAME} Account</h1>
        <AuthForm
          email={email}
          password={password}
          confirmPassword={confirmPassword}
          error={error}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onSubmit={handleRegister}
          buttonText="Register"
          isLoading={isLoading}
        >
        </AuthForm>
        <div className="login-divider">or</div>
        <GoogleAuth theme={theme} onSuccess={async (credentials) => await onGoogleAuthenticationSuccess(credentials.credential ?? '')} onError={() => setError("Something went wrong with Google Authentication. Please try again!")} />
      </div>
    </>
  );
};

export default Register;
